import { effect, untracked } from '@angular/core';
import type { FilterOption, RequestStatus } from '@indutrust/shared-model';
import type { EntityId } from '@ngrx/signals/entities';

/**
 * Create an Angular signal effect that updates a filter's options when data is loaded.
 *
 * When the provided dataStore signals a 'success' {@link RequestStatus}, this effect maps
 * entities to {@link FilterOption}[] using the provided mapper and pushes them to the filter store.
 *
 * @template Entity The entity model type contained in the data store.
 * @param filterId Identifier of the filter to update.
 * @param dataStore Object exposing requestStatus and ordered entities selectors.
 * @param dataStore.loadAllStatus Selector returning the current load status.
 * @param dataStore.entitiesOrdered Selector returning entities in display order.
 * @param filterStore Store exposing an updateFilterData method to set options.
 * @param filterStore.updateFilterData Method to push the new options to the filter store.
 * @param dataMapper Function mapping entities to filter options.
 * @returns A cleanup function returned by Angular's {@link effect}.
 * @example
 * const stop = createFilterUpdateEffect('industries', industryStore, filterStore, mapIndustries);
 */
export function createFilterUpdateEffect<Entity>(
  filterId: string,
  dataStore: {
    loadAllStatus: () => RequestStatus;
    entitiesOrdered: () => Entity[];
  },
  filterStore: {
    updateFilterData: (filterId: EntityId, data: FilterOption[]) => void;
  },
  dataMapper: (entities: Entity[]) => FilterOption[]
) {
  return effect(() => {
    const loadStatus = dataStore.loadAllStatus();
    untracked(() => {
      if (loadStatus === 'success') {
        const filterData = dataMapper(dataStore.entitiesOrdered());
        filterStore.updateFilterData(filterId, filterData);
      }
    });
  });
}
